import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ActivitiesCategories from '@BUP/components/Resources/Categories/Activities'
import ResourceSearch from '@BUP/components/Resources/ResourceSearch'
import styles from './resources.module.scss'
import { useLocation } from 'react-router-dom'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { LocationContext } from '@BUP/contexts/LocationContext'
import { CourseContext } from '@BUP/contexts/CourseContext'
import { RoleContext } from '@BUP/contexts/RoleContext'
import { STUDENT, PARENT } from '@BUP/constants'
import { resolveOwnLevelTag } from '@BUP/utils/topicResources'
import Tooltip from '@BUP/components/Generics/tooltip'

const ResourcesBySection = ({ roles, topics = [] }) => {
  const { freemium, location: { country } } = useContext(OrganizationContext)
  const locale = useContext(LocationContext)
  const course = useContext(CourseContext)
  const currentRole = useContext(RoleContext)
  const isCountryUSA = country === 'USA'

  const sections = [
    ...roles.map(role => ({ name: role, tag: `role:${role}` })),
    ...topics.map(topic => ({ name: topic, tag: `topic:${topic}` }))
  ]
  const removeFamilies = sections.filter(section => section.name !== 'parent')
  const dataToRemove = isCountryUSA ? removeFamilies : sections
  // 'assistant' never gets its own tab (it reuses the counselor's tags/copy - see the i18n
  // `role.assistant` entry aliasing `role.counselor`) - excluded here too so it's never the
  // section a user lands on with no visible tab actually marked as selected.
  const visibleSections = dataToRemove.filter(section => section.name !== 'assistant')

  const location = useLocation()
  const { selectedSection: previouslySelectedSection } = location.state || {}
  const i18n = useTranslation('translation', { keyPrefix: 'pages.resources.section' })
  // Default to the first visible section rather than requiring a click, so the category cards and
  // the resource search are visible on landing instead of just a bare row of section tabs -
  // switching tabs afterward still works exactly as before.
  const [selectedSection, setSelectedSection] = useState(previouslySelectedSection || visibleSections[0] || null)
  const handleSectionClick = event => {
    const { dataset } = event.currentTarget
    setSelectedSection(dataset)
  }
  const sectionsSelector = visibleSections.map(({ name, tag }) => {
    const activeOrNormal = selectedSection?.name === name ? 'active' : 'normal'
    const validateUSAData = isCountryUSA && freemium && name !== 'counselor'
    const disabledCard = validateUSAData ? `${styles.resources_card_topic_disabled}` : `${styles.resources_card_topic} ${styles[activeOrNormal]}`
    return (
      <Tooltip key={name} text='Upgrade with subscription' visible={validateUSAData}>
        <section
          className={disabledCard}
          onClick={validateUSAData ? null : handleSectionClick}
          data-name={name}
          data-tag={tag}
        >
          <h3>{i18n.t(`${tag.split(':').join('.')}.title`)}</h3>
          <p>{i18n.t(`${tag.split(':').join('.')}.description`)}</p>
        </section>
      </Tooltip>
    )
  })

  const selectedSectionResources = selectedSection ? <ActivitiesCategories selectedSection={selectedSection} /> : null

  // The search box only makes sense for the role-based sections (bienestar/convivencia/educación
  // digital/participación/internet/documentos, all reachable via a 'role:x' tag) - the
  // 'topic:classroom-activities' section is a different flow entirely (a curriculum topic picker,
  // not a fixed set of categories - see ClassRoomActivitiesPage), with nothing to search here.
  // Base scope mirrors ActivitiesCategories' own per-role tag-building exactly, minus the
  // `category:` tag, so every category is searched at once instead of just one.
  const isRoleSection = selectedSection?.tag?.startsWith('role:')
  const searchScopeTags = isRoleSection
    ? [`locale:${locale?.locale}`, selectedSection.tag,
        ...([STUDENT, PARENT].includes(currentRole.type) ? [resolveOwnLevelTag(course)].filter(Boolean) : [])]
    : null

  return (
    <>
      {searchScopeTags && <ResourceSearch tags={searchScopeTags} />}
      {sectionsSelector.length > 1 && (
        <div className={styles.resources_card_topic_wrapper}>
          {sectionsSelector}
        </div>
      )}
      {selectedSectionResources && (
        <div className={styles.category_section}>
          {selectedSectionResources}
        </div>
      )}
    </>
  )
}

export default ResourcesBySection
